import type { Uuid } from "@/domains/global/identifiers";
import type { UploadCompletionResult } from "@/store/audio/uploading/audio-uploading.types";

type UploadCompletionResolver = (result: UploadCompletionResult) => void;

export const UPLOAD_RESET_ERROR_MESSAGE = "Upload reset";

export class AudioUploadCompletionCoordinator {
  private pendingResolversByRecording = new Map<Uuid, UploadCompletionResolver[]>();
  private completionByRecording = new Map<Uuid, UploadCompletionResult>();

  waitFor({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<UploadCompletionResult> {
    const completedResult = this.completionByRecording.get(audioRecordingId);
    if (completedResult) {
      this.completionByRecording.delete(audioRecordingId);
      return Promise.resolve(completedResult);
    }

    return new Promise(resolve => {
      const resolvers = this.pendingResolversByRecording.get(audioRecordingId) ?? [];
      resolvers.push(resolve);
      this.pendingResolversByRecording.set(audioRecordingId, resolvers);
    });
  }

  resolve({
    audioRecordingId,
    result,
  }: {
    audioRecordingId: Uuid;
    result: UploadCompletionResult;
  }): void {
    const resolvers = this.pendingResolversByRecording.get(audioRecordingId);
    if (resolvers?.length) {
      for (const resolve of resolvers) {
        resolve(result);
      }
      this.pendingResolversByRecording.delete(audioRecordingId);
      this.completionByRecording.delete(audioRecordingId);
      return;
    }

    this.completionByRecording.set(audioRecordingId, result);
  }

  clearResult({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    this.completionByRecording.delete(audioRecordingId);
  }

  rejectAll(): void {
    const result: UploadCompletionResult = { success: false, error: UPLOAD_RESET_ERROR_MESSAGE };

    for (const resolvers of this.pendingResolversByRecording.values()) {
      for (const resolve of resolvers) {
        resolve(result);
      }
    }
    this.pendingResolversByRecording.clear();
    this.completionByRecording.clear();
  }
}
